import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Download, Edit3, X, AlertCircle, CheckCircle, Maximize2, Minimize2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';
import { showApiError } from '../../utils/apiError';

interface DrawIOClientEditorProps {
  mapId: number;
  mapTitle: string;
  onDiagramExported?: (diagramData: { format: string; data: string; filename: string }) => void;
  className?: string;
}

interface DrawIOMessage {
  event: string;
  data?: any;
  xml?: string;
  format?: string;
  message?: string;
  error?: string;
}

const DrawIOClientEditor: React.FC<DrawIOClientEditorProps> = ({
  mapId,
  mapTitle,
  onDiagramExported,
  className = ''
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editorReady, setEditorReady] = useState(false);
  const [exportFormat, setExportFormat] = useState<'png' | 'svg' | 'xml'>('png');
  const [lastExported, setLastExported] = useState<any>(null);
  const [processMapData, setProcessMapData] = useState<any>(null);
  const [mxGraphXML, setMxGraphXML] = useState<string>('');
  const [embedUrl, setEmbedUrl] = useState<string>('https://embed.diagrams.net');
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [layoutMode] = useState<'horizontal' | 'vertical'>('horizontal');
  const [exportBackground, setExportBackground] = useState<'white' | 'transparent'>('white');

  // Fetch Draw.io configuration from backend
  const fetchDrawIOConfig = async () => {
    try {
      setIsLoading(true);

      const response = await api.post(`/api/process-maps/${mapId}/generate-professional-diagram`, {
        format: 'png',
        width: layoutMode === 'horizontal' ? 4000 : 2800,  // Dynamic width based on layout
        height: layoutMode === 'horizontal' ? 1600 : 2000, // Dynamic height based on layout
        layout: layoutMode, // Use selected layout mode
        title: mapTitle
      });


      if (response.data.success) {
        const xmlData = response.data.clientConfig.mxGraphXML;
        if (xmlData && typeof xmlData === 'string' && xmlData.length > 0) {
          setMxGraphXML(xmlData);
        } else {
          toast.error('Invalid XML data received from backend');
          return;
        }

        setEmbedUrl(response.data.clientConfig.embedUrl || 'https://embed.diagrams.net');
        setProcessMapData({
          title: mapTitle,
          processes: response.data.processMap.processCount || 0,
          swimlanes: response.data.processMap.swimlaneCount || 0
        });
      } else {
        toast.error('Backend failed to generate diagram configuration');
      }
    } catch (error: any) {
      showApiError(error, 'Failed to load diagram configuration');
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize component
  useEffect(() => {
    if (mapId > 0) {
      fetchDrawIOConfig();
    }
  }, [mapId]);

  // Add timeout for iframe loading and auto-open editor
  useEffect(() => {
    if (processMapData && mxGraphXML && !isEditorOpen) {
      console.log('🚀 Configuration ready, opening Draw.io editor...');
      setIsEditorOpen(true);
      setIsLoading(true);
    }
  }, [processMapData, mxGraphXML]);

  // ESC key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isFullscreen]);


  // Draw.io embed configuration
  const DRAWIO_CONFIG = {
    embed: 1,
    ui: 'kennedy', // Modern UI
    dark: 0,       // Pin the light theme: the embed otherwise follows the OS setting on first load
    proto: 'json',
    spin: 1,
    modified: 'unsavedChanges',
    keepalive: 1,
    noSaveBtn: 0,
    saveAndExit: 1,
    noExitBtn: 0
  };

  useEffect(() => {
    // Set up message listener for Draw.io communication
    const handleMessage = (event: MessageEvent) => {
      // Accept messages from Draw.io domains and localhost for development
      const validOrigins = ['diagrams.net', 'draw.io', 'embed.diagrams.net', 'localhost'];
      const isValidOrigin = validOrigins.some(domain => event.origin.includes(domain));

      if (!isValidOrigin) return;

      try {
        const message: DrawIOMessage = typeof event.data === 'string'
          ? JSON.parse(event.data)
          : event.data;


        switch (message.event) {
          case 'init':
            setEditorReady(true);
            setIsLoading(false);
            sendDiagramToEditor();
            break;

          case 'load':
            toast.success('Draw.io editor ready!', { icon: '🎨' });
            break;

          case 'save':
            handleDiagramSaved(message);
            break;

          case 'export':
            handleDiagramExported(message);
            break;

          case 'exit':
            setIsEditorOpen(false);
            break;

          case 'error':
            toast.error(`Draw.io error: ${message.error || message.message || 'Unknown error'}`);
            break;

          default:
            // Ignore unknown events
        }
      } catch (error) {
        // Silently ignore invalid messages
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [mxGraphXML]); // Added mxGraphXML as dependency


  const openDrawIOEditor = () => {
    if (!processMapData) {
      toast.error('No process map data available');
      return;
    }

    setIsLoading(true);
    setIsEditorOpen(true);
    setEditorReady(false);
  };

  const sendDiagramToEditor = useCallback(() => {
    if (!iframeRef.current?.contentWindow) {
      toast.error('Draw.io editor not ready');
      setIsLoading(false);
      return;
    }

    if (!mxGraphXML) {
      toast.error('No diagram data available to load');
      setIsLoading(false);
      return;
    }

    try {
      const message = {
        action: 'load',
        xml: mxGraphXML,
        autosave: 0
      };

      iframeRef.current.contentWindow.postMessage(JSON.stringify(message), '*');
    } catch (error: any) {
      showApiError(error, 'Failed to load diagram');
      setIsLoading(false);
    }
  }, [mxGraphXML, processMapData, isLoading]);

  const exportDiagram = (format: 'png' | 'svg' | 'xml') => {
    if (!iframeRef.current || !editorReady) {
      toast.error('Draw.io editor not ready');
      return;
    }

    // Set export format for the handler
    setExportFormat(format);

    // Enhanced PNG export with proper background and settings
    const message = {
      action: 'export',
      format: 'png',
      bg: exportBackground === 'white' ? '#ffffff' : null,        // White or transparent background
      transparent: exportBackground === 'transparent',            // Dynamic transparency setting
      scale: 2,            // Higher resolution for better quality
      border: 20,          // More border space around diagram
      grid: false,         // Remove grid lines from export
      shadow: true         // Keep shadows for better visual depth
    };
    iframeRef.current.contentWindow?.postMessage(JSON.stringify(message), '*');
  };

  const handleDiagramSaved = (message: DrawIOMessage) => {
    if (message.xml) {
      toast.success('Diagram saved successfully!');

      const exportData = {
        format: 'xml',
        data: message.xml,
        filename: `process-map-${Date.now()}.xml`
      };

      setLastExported(exportData);
      onDiagramExported?.(exportData);
    }
  };

  const handleDiagramExported = (message: DrawIOMessage) => {
    // Only handle PNG exports for production
    if (message.data) {
      const exportData = {
        format: 'png',
        data: message.data,
        filename: `process-map-${Date.now()}.png`
      };

      toast.success('PNG diagram exported successfully!');
      downloadDiagram(exportData);
      setLastExported(exportData);
      onDiagramExported?.(exportData);
    } else {
      toast.error('Export failed - no data received');
    }
  };

  const downloadDiagram = (exportData: { format: string; data: string; filename: string }) => {
    try {
      const link = document.createElement('a');

      // Handle PNG export data
      if (exportData.data.startsWith('data:image/png;base64,')) {
        link.href = exportData.data;
      } else {
        link.href = `data:image/png;base64,${exportData.data}`;
      }

      link.download = exportData.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      showApiError(error, 'Download failed');
    }
  };

  const closeEditor = () => {
    setIsEditorOpen(false);
    setEditorReady(false);
    setIsLoading(false);
    setIsFullscreen(false);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Layout functions commented out until fully working
  // const refreshLayout = () => { ... };
  // const applyAutomaticLayout = () => { ... };

  // Export to Visio functionality
  const exportToVisio = async () => {
    try {
      const loadingToast = toast.loading('Generating Visio export...', { icon: '📊' });

      const response = await api.post(`/api/process-maps/${mapId}/export-visio`, {
        layout: layoutMode,
        title: mapTitle
      });

      if (response.data.success) {
        // Create and download the VSDX file
        const vsdxContent = response.data.vsdxXML;
        const filename = response.data.filename;

        // Create blob and download
        const blob = new Blob([vsdxContent], {
          type: 'text/csv'
        });

        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        toast.dismiss(loadingToast);
        toast.success(`Visio file exported: ${filename}`, {
          icon: '📊',
          duration: 4000
        });

        // Show export summary
        toast.success(
          `Export complete: ${response.data.processCount} processes, ${response.data.swimlaneCount} swimlanes`,
          { duration: 3000, icon: '✅' }
        );

      } else {
        throw new Error('Visio export failed');
      }

    } catch (error: any) {
      showApiError(error, 'Visio export failed', {
        duration: 6000
      });
    }
  };

  const buildDrawIOUrl = (): string => {
    const params = new URLSearchParams(DRAWIO_CONFIG as any);
    return `${embedUrl}?${params.toString()}`;
  };

  if (isLoading && !isEditorOpen) {
    return (
      <div className={`draw-io-client-editor ${className}`}>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Preparing Professional Diagram</h3>
          <p className="text-blue-700 mb-2">Loading Draw.io configuration and process map data...</p>
          <p className="text-sm text-blue-600">This may take a moment for complex process maps. Please be patient.</p>
        </div>
      </div>
    );
  }

  if (!processMapData || !mxGraphXML) {
    return (
      <div className={`draw-io-client-editor ${className}`}>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No process map data available for Draw.io editing</p>
          <button
            onClick={fetchDrawIOConfig}
            className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry Loading
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`draw-io-client-editor ${className}`}>

      {/* Control Panel */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${editorReady ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
            <h3 className="text-lg font-semibold text-gray-900">Professional Editor</h3>
            {editorReady && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <CheckCircle className="w-3 h-3 mr-1" />
                Ready
              </span>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {/* Layout Controls commented out until fully working */}

            {!isEditorOpen ? (
              <button
                onClick={openDrawIOEditor}
                disabled={isLoading}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Loading...
                  </>
                ) : (
                  <>
                    <Edit3 className="w-4 h-4 mr-2" />
                    Edit in Draw.io
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={closeEditor}
                className="inline-flex items-center px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <X className="w-4 h-4 mr-2" />
                Close Editor
              </button>
            )}
          </div>
        </div>

        {/* Export Controls */}
        {isEditorOpen && editorReady && (
          <div className="mt-4 flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">Format:</span>
                <select
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value as any)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="png">PNG (Image)</option>
                  {/* <option value="svg">SVG (Vector)</option> */}
                  {/* <option value="xml">XML (Editable)</option> */}
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">Background:</span>
                <select
                  value={exportBackground}
                  onChange={(e) => setExportBackground(e.target.value as any)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="white">White Background</option>
                  <option value="transparent">Transparent</option>
                </select>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => exportDiagram(exportFormat)}
                className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download className="w-4 h-4 mr-2" />
                Export {exportFormat.toUpperCase()}
              </button>

              <button
                onClick={exportToVisio}
                className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                title="Export structured data for Microsoft Visio (CSV format)"
              >
                📊 CSV (Visio)
              </button>
            </div>
          </div>
        )}
        {/* Standalone Visio Export */}
        {processMapData && !isEditorOpen && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-blue-900">Export Options</h4>
                <p className="text-sm text-blue-700">Export your process map to different formats</p>
              </div>
              <button
                onClick={exportToVisio}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                title="Export structured data for Microsoft Visio (CSV format)"
              >
                📊 Export to CSV (Visio)
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Draw.io Editor Iframe */}
      {isEditorOpen && (
        <div className={`bg-white border border-gray-200 rounded-lg overflow-hidden ${
          isFullscreen ? 'fixed inset-0 z-50 rounded-none' : ''
        }`}>
          <div className="h-16 bg-gray-50 border-b border-gray-200 flex items-center justify-between px-4">
            <div className="flex items-center space-x-2 text-gray-600">
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span>Loading professional editor... This may take a moment for large process maps. Please wait.</span>
                </>
              ) : (
                <>
                  <Edit3 className="w-4 h-4" />
                  <span>Professional Editor - Edit your process map</span>
                </>
              )}
            </div>

            {/* Fullscreen Controls */}
            <div className="flex items-center space-x-2">
              <button
                onClick={toggleFullscreen}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
              >
                {isFullscreen ? (
                  <Minimize2 className="w-5 h-5" />
                ) : (
                  <Maximize2 className="w-5 h-5" />
                )}
              </button>
              <button
                onClick={closeEditor}
                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Close Editor"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <iframe
            ref={iframeRef}
            src={buildDrawIOUrl()}
            className={`w-full border-0 ${
              isFullscreen ? 'h-[calc(100vh-4rem)]' : 'h-[600px]'
            }`}
            title="Draw.io Editor"
            allow="autoplay; camera; microphone; geolocation;"
            onLoad={() => {
              // Iframe loaded successfully - Draw.io will send init message
            }}
            onError={() => {
              toast.error('Failed to load Draw.io editor');
              setIsLoading(false);
            }}
          />
        </div>
      )}

      {/* Export Status */}
      {lastExported && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-sm font-medium text-green-900">
              Last Export: {lastExported.filename} ({lastExported.format.toUpperCase()})
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrawIOClientEditor;